import { z } from "zod/v3";
import { AnonymousReportStatusSchema } from "../generated/enums";
import { RunbookInputFileSchema, type WorkflowParamDTO } from "./workflow.dto";

export const ANONYMOUS_REPORT_MODULE_KEY = "ANONYMOUS_REPORTING";
export const ANONYMOUS_REPORT_FORM_PARAMS: Record<string, WorkflowParamDTO> = {
  report_content: {
    type: "text",
    value: "",
    required: true,
    displayOrder: 0,
    description:
      "Describe what happened and the concerns you would like to raise. Do not include identifying information you do not wish to disclose.",
  },
  attachments: {
    type: "files",
    value: [],
    required: false,
    displayOrder: 1,
    description:
      "Upload relevant documents or screenshots. Check the contents, filenames, and file properties for identifying information you do not wish to disclose.",
  },
};

// Strip all undeclared fields, including identity and request metadata.
export const AnonymousReportInputSchema = z.object({
  report_content: z.string().trim().min(1).max(20000),
  attachments: RunbookInputFileSchema.array().max(10).default([]),
});
export type AnonymousReportInput = z.infer<typeof AnonymousReportInputSchema>;
export const ANONYMOUS_REPORT_STATUS_LABELS = {
  OPEN: "Not started",
  IN_PROGRESS: "In progress",
  RESOLVED: "Closed",
} as const;
export const AnonymousReportUpdateSchema = z
  .object({
    id: z.string().min(1),
    closed: z.boolean().optional(),
    assigneeId: z.string().min(1).nullable().optional(),
    handlingResult: z.string().trim().max(20000).optional(),
  })
  .refine(
    (input) =>
      input.assigneeId !== undefined ||
      input.closed !== undefined ||
      input.handlingResult !== undefined,
    { message: "No report changes provided" },
  )
  .refine(
    (input) =>
      !input.closed ||
      input.handlingResult === undefined ||
      input.handlingResult.length > 0,
    {
      message: "Add the handling result before resolving this report",
      path: ["handlingResult"],
    },
  );
export type AnonymousReportUpdate = z.infer<typeof AnonymousReportUpdateSchema>;
export const AnonymousReportDTOSchema = z.object({
  id: z.string(),
  status: AnonymousReportStatusSchema,
  assigneeId: z.string().nullable(),
  assignee: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      email: z.string(),
      image: z.string().nullable().optional(),
    })
    .nullable(),
  completedAt: z.coerce.date().nullable(),
  handlingResult: z.string().nullable(),
  reportContent: z.string(),
  attachments: RunbookInputFileSchema.array(),
  createdAt: z.coerce.date(),
});
export type AnonymousReportDTO = z.infer<typeof AnonymousReportDTOSchema>;
export const AnonymousReportFormDTOSchema = z.object({ title: z.string() });
